import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { default as Table } from 'antd/es/table';
import { useEffect, useState } from 'react';

import { hasValue } from 'utils/hasValue';

import {
  useGetCryptoExchanges,
  type GetCryptoExchanges200DataItem
} from 'api/customerApi';

import { useOrganisationId } from 'hooks/context';

import { ExchangeDetailModal } from './ExchangeDetailModal';
import { tradeColumns } from './ExchangeTableColumns';

import { useVirtualBankAccount } from '../shared/VirtualBankAccountProvider';

type PaginationState = {
  page: number;
  cursorAfter?: string;
  cursorBefore?: string;
};

type Props = {
  isActive?: boolean;
};

const INITIAL_PAGINATION: PaginationState = {
  page: 1,
  cursorAfter: undefined,
  cursorBefore: undefined
};

export const ExchangesTable = ({ isActive = true }: Props) => {
  const organisationId = useOrganisationId<number>();
  const { virtualAccountNumber } = useVirtualBankAccount();
  const [pagination, setPagination] =
    useState<PaginationState>(INITIAL_PAGINATION);
  const [selectedTrade, setSelectedTrade] =
    useState<GetCryptoExchanges200DataItem | null>(null);

  useEffect(() => {
    setPagination(INITIAL_PAGINATION);
  }, [organisationId, virtualAccountNumber]);

  const { data: exchanges, isFetching: isExchangesFetching } =
    useGetCryptoExchanges(
      organisationId,
      {
        per_page: 8,
        cursor_after: pagination.cursorAfter,
        cursor_before: pagination.cursorBefore,
        virtual_account_number: virtualAccountNumber
      },
      {
        query: {
          enabled: isActive && hasValue(virtualAccountNumber),
          refetchInterval: pagination.page === 1 && isActive ? 10_000 : false,
          refetchIntervalInBackground: false
        }
      }
    );

  const nextCursor = exchanges?.meta?.cursor_next;
  const previousCursor = exchanges?.meta?.cursor_prev;
  const totalPages = exchanges?.meta?.total
    ? Math.ceil(exchanges.meta.total / (exchanges.meta.per_page ?? 1))
    : undefined;

  useEffect(() => {
    if (
      (pagination.cursorAfter || pagination.cursorBefore) &&
      !isExchangesFetching &&
      exchanges?.data?.length === 0
    ) {
      setPagination(INITIAL_PAGINATION);
    }
  }, [
    exchanges?.data?.length,
    isExchangesFetching,
    pagination.cursorAfter,
    pagination.cursorBefore
  ]);

  const handleNext = () => {
    if (nextCursor) {
      setPagination((prev) => ({
        page: prev.page + 1,
        cursorAfter: nextCursor,
        cursorBefore: undefined
      }));
    }
  };

  const handlePrevious = () => {
    if (previousCursor) {
      setPagination((prev) => {
        const newPage = Math.max(prev.page - 1, 1);
        if (newPage === 1) return INITIAL_PAGINATION;
        return {
          page: newPage,
          cursorAfter: undefined,
          cursorBefore: previousCursor
        };
      });
    }
  };

  return (
    <div>
      <Table<GetCryptoExchanges200DataItem>
        dataSource={exchanges?.data}
        columns={tradeColumns}
        className="h-[500px] overflow-auto"
        rowKey="id"
        size="small"
        pagination={false}
        loading={isExchangesFetching}
        onRow={(record) => ({
          className: 'cursor-pointer',
          onClick: () => setSelectedTrade(record)
        })}
      />
      {selectedTrade && (
        <ExchangeDetailModal
          trade={selectedTrade}
          onClose={() => setSelectedTrade(null)}
        />
      )}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-2">
          <LeftOutlined
            disabled={!exchanges?.meta?.has_prev_page}
            onClick={handlePrevious}
          />
          <RightOutlined
            disabled={!exchanges?.meta?.has_next_page}
            onClick={handleNext}
          />
          <span className="text-sm text-gray-500">
            {totalPages && `${pagination.page} of ${totalPages}`}
          </span>
        </div>
        {exchanges?.meta?.total && (
          <span className="my-2 text-sm text-gray-500">
            total: {exchanges.meta.total}
          </span>
        )}
      </div>
    </div>
  );
};
